// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Entitlement } from '@api/constants';
import {
  getAgentVersionReport,
  getEnrollmentActivityReport,
  getEpcsAuditReport,
  getLoginActivityReport,
  getUserEnrollmentReport,
} from '@api/portalServices';
import type { TenantInfoResponse } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import type { FeatureFlagKey } from '@featureFlags/registry';
import { ROLE_TYPE_ADMIN, type RouteArea } from '@portal/constants';
import { toIsoDateString, utcRangeBounds } from '@utils/utils';
import type en from '@/i18n/translations/en.json';
import { featureSupported } from '../../../featureFlagsUtils';

type En = typeof en;

type TextKeysUnder<Root extends string, Node> = `${Root}.${{
  [Key in keyof Node & string]: Node[Key] extends string ? Key : never;
}[keyof Node & string]}`;

export type ItemTextKey =
  | TextKeysUnder<'portal.settings.tabs-title', En['portal']['settings']['tabs-title']>
  | TextKeysUnder<'portal.settings.nav', En['portal']['settings']['nav']>
  | TextKeysUnder<'portal.settings.overview', En['portal']['settings']['overview']>
  | TextKeysUnder<'portal.eamc.security', En['portal']['eamc']['security']>
  | TextKeysUnder<'portal.password-policy', En['portal']['password-policy']>
  | 'portal.appearance.org-consent-title';

const TABS_TITLE = 'portal.settings.tabs-title';

export type ItemOwner = RouteArea | 'both';

export type ItemGate = {
  role: typeof ROLE_TYPE_ADMIN;
  entitlements: readonly Entitlement[];
  flags: readonly FeatureFlagKey[];
};

export const adminGate = (
  entitlements: readonly Entitlement[] = [],
  flags: readonly FeatureFlagKey[] = [],
): ItemGate => ({ role: ROLE_TYPE_ADMIN, entitlements, flags });

export const EAMC_ACCESS = adminGate([Entitlement.SSO]);

export const isAvailable = (
  gate: ItemGate,
  session: { roleType?: string; tenantInfo?: TenantInfoResponse },
): boolean =>
  session.roleType?.toLowerCase() === gate.role &&
  gate.entitlements.every(entitlement => hasEntitlement(session.tenantInfo, entitlement)) &&
  gate.flags.every(featureSupported);

export const useIsAvailable = () => {
  const { userInfo, tenantInfo } = useSession();
  return (gate: ItemGate) => isAvailable(gate, { roleType: userInfo?.roleType, tenantInfo });
};

export const REPORT_KEYS = [
  'endpoint-data',
  'epcs-activity',
  'login-activity',
  'user-data',
  'enrollment-activity',
] as const;

export type ReportKey = (typeof REPORT_KEYS)[number];

export const isReportKey = (value: string | null): value is ReportKey =>
  REPORT_KEYS.some(key => key === value);

export const REPORT_GATES: Readonly<Record<ReportKey, ItemGate>> = {
  'endpoint-data': adminGate([Entitlement.SSO]),
  'epcs-activity': adminGate([Entitlement.EPCS], ['ENABLE_EPCS']),
  'login-activity': adminGate([], ['ENABLE_LOGIN_ACTIVITY_REPORT']),
  'user-data': adminGate(),
  'enrollment-activity': adminGate([], ['ENABLE_ENROLLMENT_ACTIVITY_REPORT']),
};

export type ReportItem = {
  key: ReportKey;
  owner: ItemOwner;
  gate: ItemGate;
  labelKey: ItemTextKey;
  fileName: string;
} & (
  | { range: 'none'; fetch: () => Promise<Blob> }
  | { range: 'dates'; fetch: (range: { from: Date; to: Date }) => Promise<Blob> }
);

export const REPORT_ITEMS: readonly ReportItem[] = [
  {
    key: 'endpoint-data',
    owner: 'eamc',
    gate: REPORT_GATES['endpoint-data'],
    labelKey: `${TABS_TITLE}.endpoint-data-report`,
    fileName: 'endpoint-data-report.csv',
    range: 'none',
    fetch: getAgentVersionReport,
  },
  {
    key: 'epcs-activity',
    owner: 'eamc',
    gate: REPORT_GATES['epcs-activity'],
    labelKey: `${TABS_TITLE}.epcs-activity-report`,
    fileName: 'epcs-activity-report.csv',
    range: 'dates',
    fetch: ({ from, to }) =>
      getEpcsAuditReport({ startDate: toIsoDateString(from), endDate: toIsoDateString(to) }),
  },
  {
    key: 'login-activity',
    owner: 'both',
    gate: REPORT_GATES['login-activity'],
    labelKey: `${TABS_TITLE}.login-activity-report`,
    fileName: 'login-activity-report.csv',
    range: 'dates',
    fetch: ({ from, to }) =>
      getLoginActivityReport({ startDate: toIsoDateString(from), endDate: toIsoDateString(to) }),
  },
  {
    key: 'user-data',
    owner: 'both',
    gate: REPORT_GATES['user-data'],
    labelKey: `${TABS_TITLE}.user-data-report`,
    fileName: 'user-data-report.csv',
    range: 'none',
    fetch: getUserEnrollmentReport,
  },
  {
    key: 'enrollment-activity',
    owner: 'both',
    gate: REPORT_GATES['enrollment-activity'],
    labelKey: `${TABS_TITLE}.enrollment-activity-report`,
    fileName: 'enrollment-activity-report.csv',
    range: 'dates',
    fetch: ({ from, to }) => getEnrollmentActivityReport(utcRangeBounds(from, to, 30)),
  },
];
